const STORAGE_KEY = "uag_smart_invoice_records_v1";

const DEFAULT_CURRENCY = "ZMW";
const DEMO_INVOICE_IDS = new Set(["si_1001", "si_1002", "si_1003", "si_1004"]);
const SMART_INVOICE_DATA_SOURCE = process.env.REACT_APP_SMART_INVOICE_DATA_SOURCE || "mock";
const SMART_INVOICE_PROVIDER = "chrilan";
const SMART_INVOICE_ROUTE = "/invoice/create";
const SMART_INVOICE_USER = "frontend-webapp";
const MOCK_DELAY_MS = Number(process.env.REACT_APP_SMART_INVOICE_MOCK_DELAY_MS || 120);

const STATUS = {
  DRAFT: "draft",
  PENDING: "pending",
  PENDING_RETRY: "pending_retry",
  VALIDATED: "validated",
  FAILED: "failed",
  REFUNDED: "refunded",
};

const SUPPORTED_CURRENCIES = [
  { code: "ZMW", label: "Zambian Kwacha" },
  { code: "USD", label: "US Dollar" },
  { code: "ZAR", label: "South African Rand" },
];

const toNum = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const nowIso = () => new Date().toISOString();

const sortByUpdatedAtDesc = (a, b) =>
  new Date(b?.updated_at || b?.created_at || 0).getTime() -
  new Date(a?.updated_at || a?.created_at || 0).getTime();

const createInvoiceNumber = () => {
  const stamp = Date.now().toString().slice(-6);
  return `SI-${new Date().getFullYear()}-${stamp}`;
};

const createLocalId = () => `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const createAuditNumber = () => Number(String(Date.now()).slice(-8));

const extractInvoiceSequence = (invoiceNumber) => {
  const digits = String(invoiceNumber || "").replace(/\D/g, "");
  if (!digits) return createAuditNumber();
  return Number(digits.slice(-8));
};

const toChrilanExpire = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 8 ? digits : "";
};

const inferPlu = (line = {}, index = 0) => {
  if (line?.plu) return String(line.plu);

  const commodityCode = String(line?.commodity || "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_-]/g, "")
    .slice(0, 32);

  return commodityCode || `LINE_${index + 1}`;
};

const buildChrilanInvoicePayloadPreview = (invoice = {}, integrationMeta = {}) => {
  const lines = Array.isArray(invoice?.invoice_lines) ? invoice.invoice_lines : [];
  const localid = integrationMeta?.localid || invoice?.localid || createLocalId();
  const auditNumber = integrationMeta?.audit_number || invoice?.audit_number || createAuditNumber();

  return {
    localid,
    auditNumber,
    invoiceNumber: extractInvoiceSequence(invoice?.invoice_number),
    cell: String(invoice?.customer_cell || ""),
    refund: false,
    tpin: String(invoice?.issuer_tpin || ""),
    user: String(invoice?.submitted_by || SMART_INVOICE_USER),
    lines: lines.map((line, index) => ({
      plu: inferPlu(line, index),
      description: String(line?.description || line?.commodity || `Line Item ${index + 1}`),
      lineTotal: toNum(line?.line_total_foreign ?? line?.line_total ?? toNum(line?.tonnage) * toNum(line?.price_per_mt)),
      discountPercent: toNum(line?.discount_percent),
      qty: toNum(line?.tonnage),
      newQty: toNum(line?.new_qty),
      expire: toChrilanExpire(line?.expire),
      recommendedRetail: toNum(line?.price_per_mt),
    })),
  };
};

const buildChrilanMappingGaps = (invoice = {}) => {
  const gaps = [];
  const lines = Array.isArray(invoice?.invoice_lines) ? invoice.invoice_lines : [];

  if (!String(invoice?.issuer_tpin || "").trim()) {
    gaps.push("Issuer TPIN for Chrilan `tpin` is not supplied by the current frontend model.");
  }

  if (!String(invoice?.customer_cell || "").trim()) {
    gaps.push("Customer cell number for Chrilan `cell` is not collected on the current form.");
  }

  lines.forEach((line, index) => {
    if (!String(line?.plu || "").trim() && !String(line?.commodity || "").trim()) {
      gaps.push(`Line ${index + 1} is missing a mapped PLU/product code.`);
    }

    if (line?.new_qty === undefined || line?.new_qty === null || line?.new_qty === "") {
      gaps.push(`Line ${index + 1} has no ` + "`newQty`" + ` source in the current UI.`);
    }

    if (!toChrilanExpire(line?.expire)) {
      gaps.push(`Line ${index + 1} has no expiry date mapped for Chrilan ` + "`expire`" + `.`);
    }
  });

  return gaps;
};

const waitForMockLatency = async () => {
  const delay = SMART_INVOICE_DATA_SOURCE === "mock" ? MOCK_DELAY_MS : 0;
  if (!delay) return;

  await new Promise((resolve) => {
    window.setTimeout(resolve, delay);
  });
};

const removeDemoInvoices = (rows) => rows.filter((row) => !DEMO_INVOICE_IDS.has(String(row?.id)));

const getStoredInvoices = () => {
  const raw = localStorage.getItem(STORAGE_KEY);

  if (!raw) {
    const emptyRows = [];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(emptyRows));
    return emptyRows;
  }

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const sanitizedRows = removeDemoInvoices(parsed);
      if (sanitizedRows.length !== parsed.length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitizedRows));
      }
      return sanitizedRows;
    }
  } catch (error) {
    console.error("Failed to parse smart invoice storage.", error);
  }

  const emptyRows = [];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(emptyRows));
  return emptyRows;
};

const setStoredInvoices = (rows) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
};

const normalizeLine = (line = {}, index = 0) => ({
  id: line?.id || `line_${Date.now()}_${index}`,
  commodity: line?.commodity || "",
  description: line?.description || "",
  tonnage: toNum(line?.tonnage),
  price_per_mt: toNum(line?.price_per_mt),
  cost_per_mt: toNum(line?.cost_per_mt),
  transport_ex_sa: toNum(line?.transport_ex_sa),
  transport_local: toNum(line?.transport_local),
  import_costs: toNum(line?.import_costs),
  commission: toNum(line?.commission),
  discount_percent: toNum(line?.discount_percent),
  new_qty: line?.new_qty === undefined || line?.new_qty === null || line?.new_qty === ""
    ? ""
    : toNum(line?.new_qty),
  expire: line?.expire || "",
  plu: line?.plu || "",
});

const buildDefaultIntegrationMeta = (record = {}) => {
  const baseMeta = {
    localid: createLocalId(),
    audit_number: createAuditNumber(),
  };
  const requestPayloadPreview = buildChrilanInvoicePayloadPreview(record, baseMeta);

  return {
    provider: SMART_INVOICE_PROVIDER,
    mode: SMART_INVOICE_DATA_SOURCE,
    route: SMART_INVOICE_ROUTE,
    state: record?.status === STATUS.DRAFT ? "draft" : "queued",
    localid: requestPayloadPreview.localid,
    audit_number: requestPayloadPreview.auditNumber,
    processed: false,
    retry_count: 0,
    code: "",
    last_attempt_at: record?.status === STATUS.DRAFT ? "" : record?.updated_at || record?.created_at || "",
    last_message:
      record?.status === STATUS.DRAFT
        ? "Draft saved locally. Backend integration pending."
        : "Submission queued in mock mode. Backend API not connected yet.",
    request_payload_preview: requestPayloadPreview,
    response_payload: null,
    mapping_gaps: buildChrilanMappingGaps(record),
  };
};

const normalizeIntegrationMeta = (record = {}) => {
  const existingMeta = record?.integration_meta || {};
  const defaultMeta = buildDefaultIntegrationMeta(record);
  const localid = existingMeta?.localid || defaultMeta.localid;
  const auditNumber = existingMeta?.audit_number || defaultMeta.audit_number;
  const merged = {
    ...defaultMeta,
    ...existingMeta,
    localid,
    audit_number: auditNumber,
  };

  return {
    ...merged,
    request_payload_preview: buildChrilanInvoicePayloadPreview(record, merged),
    mapping_gaps:
      Array.isArray(existingMeta?.mapping_gaps) && existingMeta.mapping_gaps.length
        ? existingMeta.mapping_gaps
        : buildChrilanMappingGaps(record),
    retry_count: toNum(merged.retry_count),
    processed: Boolean(merged.processed),
  };
};

const deriveInvoiceRecord = (record = {}) => {
  const currency = record?.currency || DEFAULT_CURRENCY;
  const exchangeRate = currency === DEFAULT_CURRENCY ? 1 : Math.max(toNum(record?.exchange_rate), 0);
  const safeExchangeRate = exchangeRate > 0 ? exchangeRate : 1;
  const invoiceLines = Array.isArray(record?.invoice_lines)
    ? record.invoice_lines.map((line, index) => normalizeLine(line, index))
    : [];

  const computedLines = invoiceLines.map((line) => {
    const lineTotalForeign = line.tonnage * line.price_per_mt;
    const lineTotalZmw = currency === DEFAULT_CURRENCY ? lineTotalForeign : lineTotalForeign * safeExchangeRate;
    const totalCostPerMt =
      line.cost_per_mt +
      line.transport_ex_sa +
      line.transport_local +
      line.import_costs +
      line.commission;
    const totalCost = line.tonnage * totalCostPerMt;
    const grossProfit = lineTotalZmw - totalCost;
    const marginPct = lineTotalZmw > 0 ? (grossProfit / lineTotalZmw) * 100 : 0;

    return {
      ...line,
      line_total_foreign: lineTotalForeign,
      line_total: lineTotalZmw,
      total_cost_per_mt: totalCostPerMt,
      total_cost: totalCost,
      gross_profit: grossProfit,
      margin_pct: marginPct,
    };
  });

  const subtotalForeign = computedLines.reduce((sum, line) => sum + line.line_total_foreign, 0);
  const subtotalZmw = computedLines.reduce((sum, line) => sum + line.line_total, 0);
  const vatAmountForeign = subtotalForeign * 0.16;
  const vatAmountZmw = subtotalZmw * 0.16;
  const totalDueForeign = subtotalForeign + vatAmountForeign;
  const totalDueZmw = subtotalZmw + vatAmountZmw;
  const totalGrossProfit = computedLines.reduce((sum, line) => sum + line.gross_profit, 0);

  const baseRecord = {
    ...record,
    currency,
    exchange_rate: currency === DEFAULT_CURRENCY ? 1 : safeExchangeRate,
    invoice_lines: computedLines,
    subtotal_foreign: subtotalForeign,
    subtotal: subtotalZmw,
    vat_amount_foreign: vatAmountForeign,
    vat_amount: vatAmountZmw,
    total_due_foreign: totalDueForeign,
    total_due: totalDueZmw,
    total_amount: totalDueZmw,
    total_gross_profit: totalGrossProfit,
    issued_at: record?.issued_at || "",
  };

  return {
    ...baseRecord,
    integration_meta: normalizeIntegrationMeta(baseRecord),
  };
};

const buildMockValidatedResponse = (record = {}, integrationMeta = {}) => ({
  rcptNo: extractInvoiceSequence(record?.invoice_number),
  intrlData: integrationMeta?.localid || createLocalId(),
  rcptSign: `SIGN-${Date.now().toString().slice(-8)}`,
  totRcptNo: extractInvoiceSequence(record?.invoice_number),
  vsdcRcptPbctDate: nowIso().replace(/[-:.TZ]/g, "").slice(0, 14),
  sdcId: "SDC-MOCK-0001",
  mrcNo: "MRC-MOCK-0001",
});

const getIntegrationStateForStatus = (status = STATUS.DRAFT) => {
  if (status === STATUS.PENDING) return "queued";
  if (status === STATUS.PENDING_RETRY) return "retry_pending";
  if (status === STATUS.VALIDATED) return "validated";
  if (status === STATUS.FAILED) return "failed";
  if (status === STATUS.REFUNDED) return "refund_requested";
  return "draft";
};

const buildIntegrationMessageForStatus = (status = STATUS.DRAFT) => {
  if (status === STATUS.PENDING) {
    return "Submission queued in mock mode. Backend API not connected yet.";
  }

  if (status === STATUS.PENDING_RETRY) {
    return "Retry queued in mock mode. Backend API not connected yet.";
  }

  if (status === STATUS.VALIDATED) {
    return "Submission marked as validated.";
  }

  if (status === STATUS.FAILED) {
    return "Submission failed.";
  }

  if (status === STATUS.REFUNDED) {
    return "Refund flagged on the frontend. Backend refund endpoint pending.";
  }

  return "Draft saved locally. Backend integration pending.";
};

const updateIntegrationMetaForStatus = (record = {}, status, extra = {}) => {
  const currentMeta = normalizeIntegrationMeta(record);
  const nextMeta = {
    ...currentMeta,
    state: getIntegrationStateForStatus(status),
    processed: status === STATUS.VALIDATED ? true : currentMeta.processed,
    code: status === STATUS.VALIDATED ? extra?.code || "000" : extra?.code || currentMeta.code || "",
    last_attempt_at:
      status === STATUS.DRAFT
        ? currentMeta.last_attempt_at
        : nowIso(),
    last_message: extra?.last_message || buildIntegrationMessageForStatus(status),
    retry_count:
      status === STATUS.PENDING_RETRY
        ? toNum(currentMeta.retry_count) + 1
        : toNum(currentMeta.retry_count),
  };

  if (status === STATUS.VALIDATED) {
    nextMeta.response_payload =
      extra?.response_payload || buildMockValidatedResponse(record, nextMeta);
  }

  if (extra?.response_payload === null) {
    nextMeta.response_payload = null;
  }

  return {
    ...nextMeta,
    request_payload_preview: buildChrilanInvoicePayloadPreview(record, nextMeta),
    mapping_gaps: buildChrilanMappingGaps(record),
  };
};

export const getSupportedInvoiceCurrencies = () => SUPPORTED_CURRENCIES;

export const generateSmartInvoiceNumber = () => createInvoiceNumber();

export const getSmartInvoiceDataSource = () => SMART_INVOICE_DATA_SOURCE;

export const listSmartInvoices = async () => {
  await waitForMockLatency();
  return getStoredInvoices().map(deriveInvoiceRecord).sort(sortByUpdatedAtDesc);
};

export const getSmartInvoiceById = async (invoiceId) => {
  await waitForMockLatency();

  const invoice = getStoredInvoices().find(
    (row) => String(row?.id) === String(invoiceId) || String(row?.invoice_number) === String(invoiceId),
  );

  return invoice ? deriveInvoiceRecord(invoice) : null;
};

export const createSmartInvoice = async (payload = {}, { submit = false } = {}) => {
  await waitForMockLatency();

  const rows = getStoredInvoices();
  const createdAt = nowIso();

  const newRecord = {
    id: `si_${Date.now()}`,
    invoice_number: payload?.invoice_number || createInvoiceNumber(),
    invoice_date: payload?.invoice_date || createdAt.slice(0, 10),
    created_at: createdAt,
    updated_at: createdAt,
    issued_at: submit ? new Date().toLocaleString("sv-SE").replace("T", " ") : "",
    status: submit ? STATUS.PENDING : STATUS.DRAFT,
    smart_reference: "",
    qr_code: null,
    customer_name: payload?.customer_name || "",
    customer_tpin: payload?.customer_tpin || "",
    customer_address: payload?.customer_address || "",
    customer_cell: payload?.customer_cell || "",
    issuer_tpin: payload?.issuer_tpin || "",
    submitted_by: payload?.submitted_by || SMART_INVOICE_USER,
    delivery_note_ref: payload?.delivery_note_ref || "",
    re_reference: payload?.re_reference || "",
    currency: payload?.currency || DEFAULT_CURRENCY,
    exchange_rate: payload?.currency === DEFAULT_CURRENCY ? 1 : payload?.exchange_rate,
    payment_terms: payload?.payment_terms || "",
    bank_details: payload?.bank_details || "",
    notes: payload?.notes || "",
    invoice_lines: Array.isArray(payload?.invoice_lines) ? payload.invoice_lines : [],
  };

  const derivedRecord = deriveInvoiceRecord(newRecord);
  const integrationMeta = {
    ...derivedRecord.integration_meta,
    state: submit ? "queued" : "draft",
    last_attempt_at: submit ? createdAt : "",
    last_message: buildIntegrationMessageForStatus(submit ? STATUS.PENDING : STATUS.DRAFT),
  };

  const storedRecord = {
    ...newRecord,
    integration_meta: integrationMeta,
  };

  rows.push(storedRecord);
  setStoredInvoices(rows);

  return deriveInvoiceRecord(storedRecord);
};

export const updateSmartInvoiceStatus = async (invoiceId, status, extra = {}) => {
  await waitForMockLatency();

  const rows = getStoredInvoices();
  const nextRows = rows.map((row) => {
    const matches =
      String(row?.id) === String(invoiceId) || String(row?.invoice_number) === String(invoiceId);

    if (!matches) return row;

    const nextRow = {
      ...row,
      ...extra,
      status,
      updated_at: nowIso(),
      issued_at:
        status === STATUS.VALIDATED || status === STATUS.PENDING || status === STATUS.PENDING_RETRY
          ? new Date().toLocaleString("sv-SE").replace("T", " ")
          : row?.issued_at || "",
      smart_reference:
        status === STATUS.VALIDATED
          ? extra?.smart_reference || `ZRA-SMART-${Date.now().toString().slice(-7)}`
          : extra?.smart_reference !== undefined
            ? extra.smart_reference
            : row?.smart_reference || "",
    };

    const derivedNextRow = deriveInvoiceRecord(nextRow);

    return {
      ...nextRow,
      integration_meta: updateIntegrationMetaForStatus(derivedNextRow, status, extra),
    };
  });

  setStoredInvoices(nextRows);
  return getSmartInvoiceById(invoiceId);
};

export const retrySmartInvoiceSubmission = async (invoiceId) =>
  updateSmartInvoiceStatus(invoiceId, STATUS.PENDING_RETRY, { smart_reference: "" });

export const refundSmartInvoice = async (invoiceId) =>
  updateSmartInvoiceStatus(invoiceId, STATUS.REFUNDED);
